import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import get from 'lodash/get';
import { connect } from 'react-redux';

import nds from '@nerdwallet/base-styles/classes';
import { Box } from '@nerdwallet/currency';
import Row from '../row/row';
import TwoColCreateAccount from './row-two-col-create-account';
import TwoColImage from './row-two-col-image';
import TwoColList from './row-two-col-list';
import TwoColText from './row-two-col-text';

import {
  backgroundColorPicker,
  repeatable,
  stringSelectSpec,
} from '../../lib/editing-configs';

import './row-two-col.less';

const componentMap = {
  image: TwoColImage,
  createAccount: TwoColCreateAccount,
  list: TwoColList,
  text: TwoColText,
};

const RowTwoCol = ({
  backgroundColor,
  className,
  id,
  left,
  maxWidth,
  paddingStyle,
  right,
  size,
  textColor,
  verticalAlignment,
}) => {
  const imageOrderOverrideClassname =
    get(right[0], 'subtype') === 'image' ? 'row-two-col__image-override' : '';

  const buildSectionElement = (element, i) => {
    const props = { textColor, size, key: i };

    const Component = componentMap[element.subtype] || (() => null);
    return <Component {...props} {...element} />;
  };

  const buildSide = (side) =>
    side.map((element, i) => buildSectionElement(element, i));

  const columnClasses = [
    'row-two-col__section',
    {
      // Default: vertically centered
      [nds('justify-content-center')]: verticalAlignment !== 'top',
    },
  ];

  return (
    <Row className="row-two-col" backgroundColor={backgroundColor}>
      <Row
        id={id}
        className={classNames('row-two-col__container', className)}
        columns="2"
        paddingStyle={paddingStyle}
        maxWidth={maxWidth}
        centeredInParent
        standardMaxWidths
      >
        <Box className={classNames(columnClasses, 'row-two-col__left')}>
          {buildSide(left)}
        </Box>
        <Box
          className={classNames(
            columnClasses,
            'row-two-col__right',
            imageOrderOverrideClassname
          )}
        >
          {buildSide(right)}
        </Box>
      </Row>
    </Row>
  );
};

const createAccountShape = PropTypes.shape({
  subtype: PropTypes.string,
  // eslint-disable-next-line react/forbid-foreign-prop-types -- directive added automatically by Shepherd migration
  ...TwoColCreateAccount.propTypes,
});
const imageShape = PropTypes.shape({
  subtype: PropTypes.string,
  // eslint-disable-next-line react/forbid-foreign-prop-types -- directive added automatically by Shepherd migration
  ...TwoColImage.propTypes,
});
const listShape = PropTypes.shape({
  subtype: PropTypes.string,
  // eslint-disable-next-line react/forbid-foreign-prop-types -- directive added automatically by Shepherd migration
  ...TwoColList.propTypes,
});
const textShape = PropTypes.shape({
  subtype: PropTypes.string,
  // eslint-disable-next-line react/forbid-foreign-prop-types -- directive added automatically by Shepherd migration
  ...TwoColText.propTypes,
});

RowTwoCol.propTypes = {
  id: PropTypes.string,
  identity: PropTypes.object,
  className: PropTypes.string,
  backgroundColor: PropTypes.string,
  textColor: PropTypes.string,
  paddingStyle: PropTypes.oneOf([
    '',
    'module-lg',
    'module-lg-skinny-bottom-mobile',
    'module-sm',
  ]),
  maxWidth: PropTypes.oneOf(['970px']),
  standardMaxWidths: PropTypes.bool,
  centeredInParent: PropTypes.bool,
  size: PropTypes.oneOf(['', 'sm', 'md', 'lg']),
  left: PropTypes.arrayOf(
    PropTypes.oneOfType([createAccountShape, imageShape, listShape, textShape])
  ),
  right: PropTypes.arrayOf(
    PropTypes.oneOfType([createAccountShape, imageShape, listShape, textShape])
  ),
  verticalAlignment: PropTypes.oneOf(['default', 'top']),
};

RowTwoCol.displayName = 'RowTwoCol';

RowTwoCol.defaultProps = {
  centeredInParent: true,
  className: null,
  id: null,
  paddingStyle: '',
  left: [],
  right: [],
};

const subtypeEditingSelect = stringSelectSpec(
  'subtype',
  'Select Content Type',
  [
    {
      value: '',
      label: 'Select One',
    },
    {
      label: 'Create Account',
      value: 'createAccount',
    },
    {
      label: 'Image',
      value: 'image',
    },
    {
      label: 'List',
      value: 'list',
    },
    {
      label: 'Text',
      value: 'text',
    },
  ]
);

const subtypeEditingConfigs = {
  createAccount: TwoColCreateAccount.getEditingConfig(),
  image: TwoColImage.getEditingConfig(),
  list: TwoColList.getEditingConfig(),
  text: TwoColText.getEditingConfig(),
};

RowTwoCol.getEditingConfig = (componentDocument) => [
  backgroundColorPicker,
  stringSelectSpec('verticalAlignment', 'Vertical Alignment', [
    {
      value: 'default',
      label: 'Default',
    },
    {
      value: 'top',
      label: 'Top',
    },
  ]),
  repeatable('left', 'Left Side', 'Section', (index) => {
    const subtype = get(componentDocument, `props.left[${index}].subtype`);
    const subtypeConfig = get(subtypeEditingConfigs, subtype, []);
    return [subtypeEditingSelect, ...subtypeConfig];
  }),
  repeatable('right', 'Right Side', 'Section', (index) => {
    const subtype = get(componentDocument, `props.right[${index}].subtype`);
    const subtypeConfig = get(subtypeEditingConfigs, subtype, []);
    return [subtypeEditingSelect, ...subtypeConfig];
  }),
];

export default connect(({ mediaQuery }) => ({
  size: mediaQuery,
}))(RowTwoCol);
